import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Circle, ArrowRight, Confetti } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { ONBOARDING_STEPS } from "@/data/helpContent";

// Role-specific guided onboarding checklist with persisted progress.
export function OnboardingWizard({ role, embedded = false }) {
  const api = useApi();
  const navigate = useNavigate();
  const steps = ONBOARDING_STEPS[role] || [];
  const [done, setDone] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/help/onboarding")
      .then((r) => setDone(r.data?.completed_steps || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = async (next) => {
    const prev = done;
    setDone(next);
    try { await api.put("/help/onboarding", { completed_steps: next }); }
    catch (e) { setDone(prev); toast.error("Couldn't save your progress. Please try again."); }
  };

  const toggle = (id) => {
    const next = done.includes(id) ? done.filter((x) => x !== id) : [...done, id];
    persist(next);
  };

  const completeAndGo = (step) => {
    if (!done.includes(step.id)) persist([...done, step.id]);
    if (step.cta?.path) navigate(step.cta.path);
  };

  const total = steps.length;
  const count = steps.filter((s) => done.includes(s.id)).length;
  const pct = total ? Math.round((count / total) * 100) : 0;
  const allDone = total > 0 && count === total;

  if (!loaded) return <p className="text-[#7A9090] text-sm" data-testid="onboarding-loading">Loading your checklist…</p>;

  return (
    <div className="space-y-5" data-testid="onboarding-wizard">
      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[#3C4048] font-semibold" style={{ fontFamily: "Montserrat" }}>Your setup progress</p>
          <span className="text-[#3A7A8C] text-sm font-bold" data-testid="onboarding-progress-label">{count}/{total} done</span>
        </div>
        <div className="w-full h-2.5 rounded-full bg-[#eef1f2] overflow-hidden">
          <div className="h-full bg-[#5FA8B4] transition-all duration-500" style={{ width: `${pct}%` }} data-testid="onboarding-progress-bar" />
        </div>
        {allDone && (
          <p className="mt-3 text-[#15803d] text-sm font-medium flex items-center gap-1.5" data-testid="onboarding-complete">
            <Confetti size={16} weight="fill" /> You're all set — nice work!
          </p>
        )}
      </div>

      <div className="space-y-3">
        {steps.map((step, i) => {
          const isDone = done.includes(step.id);
          return (
            <div key={step.id} data-testid={`onboarding-step-${step.id}`}
              className={`flex items-start gap-3 rounded-[12px] border p-4 transition-colors ${isDone ? "bg-[#f0f7f8] border-[#cfe6ea]" : "bg-white border-[#e5e7eb]"}`}>
              <button onClick={() => toggle(step.id)} className="mt-0.5 shrink-0 p-2 -m-2" data-testid={`onboarding-toggle-${step.id}`} aria-label="Toggle complete">
                {isDone ? <CheckCircle size={22} weight="fill" className="text-[#22c55e]" /> : <Circle size={22} className="text-[#c3d0d2]" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`font-semibold ${isDone ? "text-[#3A7A8C]" : "text-[#3C4048]"}`}>
                  <span className="text-[#7A9090] mr-1.5">{i + 1}.</span>{step.title}
                </p>
                <p className="text-[#55666b] text-sm mt-0.5">{step.desc}</p>
                {step.cta && !embedded && (
                  <Button size="sm" onClick={() => completeAndGo(step)} data-testid={`onboarding-cta-${step.id}`}
                    className="mt-3 bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] rounded-[10px] h-8 gap-1.5 text-xs">
                    {step.cta.label} <ArrowRight size={14} weight="bold" />
                  </Button>
                )}
                {step.cta && embedded && !isDone && (
                  <Button size="sm" variant="outline" onClick={() => toggle(step.id)} data-testid={`onboarding-cta-${step.id}`}
                    className="mt-3 border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#f0f7f8] rounded-[10px] h-8 text-xs">
                    Got it
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
